import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Package, Eye, ArrowRight } from "lucide-react";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { Pagination } from "@/components/common/Pagination";
import { DetailDialog, DetailRow } from "@/components/ui/detail-dialog";
import { UserName } from "@/components/common/EntityName";
import { ordersApi } from "@/lib/api";
import type { Order } from "@/lib/api/types";
import { fmtCurrency, fmtRelativeTime } from "@/lib/utils";
import { usePagedQuery, PAGE_SIZE } from "@/lib/usePagedQuery";

const STATUS_FILTERS = ["ALL", "PENDING", "CONFIRMED", "PROCESSING", "SHIPPED", "DELIVERED", "CANCELLED", "REFUNDED"] as const;

/**
 * The next steps an admin may take from each status — the same transitions
 * order-service enforces (it refuses anything else with ORD-05). REFUNDED
 * from CANCELLED only succeeds for a paid order.
 */
const NEXT_STEPS: Record<string, { to: string; label: string; tone?: "destructive" }[]> = {
  PENDING: [{ to: "CONFIRMED", label: "Confirm" }, { to: "CANCELLED", label: "Cancel", tone: "destructive" }],
  CONFIRMED: [
    { to: "PROCESSING", label: "Start processing" },
    { to: "SHIPPED", label: "Mark shipped" },
    { to: "DELIVERED", label: "Mark delivered" },
    { to: "CANCELLED", label: "Cancel", tone: "destructive" },
  ],
  PROCESSING: [
    { to: "SHIPPED", label: "Mark shipped" },
    { to: "DELIVERED", label: "Mark delivered" },
    { to: "CANCELLED", label: "Cancel", tone: "destructive" },
  ],
  SHIPPED: [{ to: "DELIVERED", label: "Mark delivered" }],
  DELIVERED: [{ to: "REFUNDED", label: "Refund", tone: "destructive" }],
  CANCELLED: [{ to: "REFUNDED", label: "Refund (paid orders)", tone: "destructive" }],
};

const STATUS_VARIANT: Record<string, "success" | "warning" | "destructive" | "muted" | "sunset"> = {
  PENDING: "warning",
  CONFIRMED: "sunset",
  PROCESSING: "sunset",
  SHIPPED: "sunset",
  DELIVERED: "success",
  COMPLETED: "success",
  CANCELLED: "muted",
};

export default function OrdersList() {
  const [filter, setFilter] = useState<(typeof STATUS_FILTERS)[number]>("ALL");
  const [detail, setDetail] = useState<Order | null>(null);
  const [step, setStep] = useState<{ order: Order; to: string; label: string; tone?: "destructive" } | null>(null);
  const [stepError, setStepError] = useState<string | null>(null);
  const qc = useQueryClient();
  const changeStatus = useMutation({
    mutationFn: async ({ id, to }: { id: string; to: string }) => {
      const res = await ordersApi.setStatus(id, to);
      // Refusals come back as HTTP 200 without the updated order.
      if (!res.data.data) throw new Error(res.data.errors?.[0]?.message ?? `The server refused this change (${res.data.code}).`);
      return res.data.data;
    },
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ["orders"] });
      setDetail(updated);
      setStep(null);
    },
    onError: (e: Error) => { setStepError(e.message); setStep(null); },
  });

  const {
    page, setPage, items, totalItems, totalPages, isFetching,
  } = usePagedQuery<Order, { status: string }>({
    key: ["orders", filter],
    fetcher: ordersApi.adminAll,
    extra: { status: filter },
  });

  return (
    <>
      <PageHeader title="Orders" subtitle={`${totalItems} marketplace orders across all sellers.`} />

      <Card>
        <CardContent className="p-0">
          <div className="flex flex-wrap gap-1.5 border-b border-border p-3">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => { setFilter(f); setPage(0); }}
                className={`cursor-pointer rounded-full border px-4 py-1.5 text-xs font-semibold transition ${
                  filter === f
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border bg-card text-muted-foreground hover:text-foreground"
                }`}
              >
                {f === "ALL" ? "All" : f.charAt(0) + f.slice(1).toLowerCase()}
              </button>
            ))}
          </div>

          {items.length === 0 ? (
            <EmptyState
              icon={Package}
              title={isFetching ? "Loading…" : "No orders"}
              description="No orders match this filter yet."
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Seller</TableHead>
                  <TableHead>Qty</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Paid</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Placed</TableHead>
                  <TableHead className="text-right">Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((o) => (
                  <TableRow key={o.id} className="cursor-pointer" onClick={() => setDetail(o)}>
                    <TableCell className="font-mono text-xs">{o.id.slice(0, 8)}…</TableCell>
                    <TableCell className="text-sm"><UserName id={o.customerId} /></TableCell>
                    <TableCell className="text-sm"><UserName id={o.sellerId} /></TableCell>
                    <TableCell className="text-sm">{o.quantity}</TableCell>
                    <TableCell className="text-sm font-semibold">{fmtCurrency(o.totalPrice, "TZS")}</TableCell>
                    <TableCell>
                      {o.isPaid ? <Badge variant="success">Paid</Badge> : <Badge variant="warning">Unpaid</Badge>}
                    </TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANT[o.status] ?? "muted"}>{o.status}</Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{fmtRelativeTime(o.createdAt)}</TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <Button size="sm" variant="outline" onClick={() => setDetail(o)}>
                        <Eye className="size-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <Pagination page={page} size={PAGE_SIZE} totalItems={totalItems} totalPages={totalPages} onChange={setPage} />
        </CardContent>
      </Card>

      <DetailDialog
        open={!!detail}
        onClose={() => setDetail(null)}
        icon={Package}
        title={detail ? fmtCurrency(detail.totalPrice, "TZS") : ""}
        subtitle={detail ? `Order ${detail.id.slice(0, 8)}… · ${detail.status}` : undefined}
        footer={
          detail && (NEXT_STEPS[detail.status]?.length ?? 0) > 0 ? (
            <div className="w-full space-y-2">
              {stepError && <p className="rounded-lg bg-destructive/10 p-2 text-sm text-destructive">{stepError}</p>}
              <div className="flex flex-wrap gap-2">
                {NEXT_STEPS[detail.status].map((s) => (
                  <Button
                    key={s.to}
                    size="sm"
                    variant="outline"
                    className={s.tone === "destructive" ? "border-destructive/40 text-destructive" : undefined}
                    onClick={() => { setStepError(null); setStep({ order: detail, ...s }); }}
                  >
                    {s.label} <ArrowRight className="size-3.5" />
                  </Button>
                ))}
              </div>
            </div>
          ) : undefined
        }
      >
        {detail && (
          <>
            <DetailRow label="Customer" value={<UserName id={detail.customerId} />} />
            <DetailRow label="Seller" value={<UserName id={detail.sellerId} />} />
            <DetailRow label="Quantity" value={String(detail.quantity)} />
            <DetailRow label="Unit price" value={fmtCurrency(detail.unitPrice, "TZS")} />
            <DetailRow label="Paid" value={detail.isPaid ? "Yes" : "No"} />
            <DetailRow label="Payment ref" value={detail.paymentReference ?? "—"} />
            <DetailRow label="Delivery" value={[detail.deliveryAddress, detail.deliveryCity].filter(Boolean).join(", ") || "—"} />
            <DetailRow label="Delivery phone" value={detail.deliveryPhone ?? "—"} />
            <DetailRow label="Placed" value={fmtRelativeTime(detail.createdAt)} />
          </>
        )}
      </DetailDialog>

      <ConfirmDialog
        open={!!step}
        onOpenChange={(v) => !v && setStep(null)}
        onConfirm={() => step && changeStatus.mutate({ id: step.order.id, to: step.to })}
        icon={Package}
        tone={step?.tone === "destructive" ? "destructive" : undefined}
        title={step ? `${step.label}?` : ""}
        description={step ? `Order ${step.order.id.slice(0, 8)}… moves from ${step.order.status} to ${step.to}. The buyer sees the new status.` : ""}
        confirmLabel={step?.label ?? ""}
        loading={changeStatus.isPending}
      />
    </>
  );
}
